/**
 * AppErrorBoundary (Feb 2026 · RACKET-LAB-PLAYER-PREVIEW-UX-1).
 *
 * React class error boundary that catches client-side render errors and
 * shows a friendly fallback instead of a white screen.
 *
 * Does NOT replace network/origin errors at the edge (Cloudflare 502/520
 * issues live below this layer) — but it does ensure that any client-side
 * exception in a child component renders a controlled UI instead of
 * crashing the whole shell.
 */
import React from "react";

class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_error) {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    try {
      // Best-effort console trace, no stack to the user.
      // eslint-disable-next-line no-console
      console.error("[AppErrorBoundary]", error, info);
    } catch {
      /* swallow */
    }
  }

  render() {
    if (this.state.hasError) {
      const lang = (typeof localStorage !== "undefined" && localStorage.getItem("tsw_lang")) || "es";
      const t = lang === "es"
        ? {
            title: "Algo no se pudo cargar correctamente",
            body: "Estamos trabajando en ello. Vuelve a intentarlo en unos segundos.",
            reload: "Recargar",
            home: "Volver al inicio",
          }
        : {
            title: "Something didn't load correctly",
            body: "We're on it. Please try again in a few seconds.",
            reload: "Reload",
            home: "Back home",
          };
      return (
        <div
          data-testid="app-error-boundary"
          className="min-h-screen flex items-center justify-center bg-slate-50 px-4"
        >
          <div className="max-w-md w-full bg-white border border-amber-200 rounded-2xl shadow-sm p-8 text-center space-y-4">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-amber-100 text-amber-700 text-xl font-bold">⚠</div>
            <h1 className="text-xl font-semibold text-slate-900" data-testid="app-error-title">
              {t.title}
            </h1>
            <p className="text-sm text-slate-600" data-testid="app-error-body">{t.body}</p>
            <div className="flex gap-2 justify-center pt-2">
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium"
                data-testid="app-error-reload"
              >
                {t.reload}
              </button>
              <a
                href="/"
                className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-medium"
                data-testid="app-error-home"
              >
                {t.home}
              </a>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default AppErrorBoundary;
